import { ArrowLeft, CircleAlert, CircleCheck, Rows2, Rows3, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from "react";

/* -------------------------------------------------------------------------- */
/* Formatting                                                                  */
/* -------------------------------------------------------------------------- */

const intFormat = new Intl.NumberFormat("en-US");
const dateFormat = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric"
});
const shortDateFormat = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });

export function fmtInt(value: number | string) {
  return intFormat.format(Number(value));
}

export function fmtSigned(value: number | string) {
  const n = Number(value);
  return `${n > 0 ? "+" : n < 0 ? "−" : ""}${intFormat.format(Math.abs(n))}`;
}

export function fmtDate(value: string) {
  return dateFormat.format(new Date(value));
}

export function fmtShortDate(value: string) {
  return shortDateFormat.format(new Date(`${value}T12:00:00Z`));
}

export function fmtRelative(value: string | null) {
  if (!value) return "Never";
  const days = Math.round((Date.now() - new Date(value).getTime()) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  return dateFormat.format(new Date(value));
}

export function humanize(value: string) {
  const spaced = value.replaceAll("_", " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function initials(name: string) {
  return name
    .split(/\s+/)
    .map((part) => part[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/* -------------------------------------------------------------------------- */
/* Page chrome                                                                 */
/* -------------------------------------------------------------------------- */

export function PageHead({
  title,
  count,
  authority,
  authorityMode = "remote",
  children
}: {
  title: string;
  count?: ReactNode;
  authority: string;
  authorityMode?: "remote" | "read" | "own";
  children?: ReactNode;
}) {
  return (
    <header className="page-head">
      <h1>{title}</h1>
      {count !== undefined && <span className="count">{count}</span>}
      <span className="head-spacer" />
      {children}
      <span className="authority" data-mode={authorityMode} title="Service that owns this data">
        <i aria-hidden="true" />
        {authority}
      </span>
    </header>
  );
}

export function DensityToggle({
  density,
  onChange
}: {
  density: Density;
  onChange: (next: Density) => void;
}) {
  return (
    <Segmented<Density>
      className="density-toggle"
      label="Row density"
      value={density}
      onChange={(next) => onChange(next)}
      options={[
        {
          value: "dense",
          title: "Dense rows",
          label: (
            <>
              <Rows3 aria-hidden="true" />
              <span className="sr-only">Dense rows</span>
            </>
          )
        },
        {
          value: "comfortable",
          title: "Comfortable rows",
          label: (
            <>
              <Rows2 aria-hidden="true" />
              <span className="sr-only">Comfortable rows</span>
            </>
          )
        }
      ]}
    />
  );
}

export type Density = "dense" | "comfortable";

export function useDensity(): [Density, (next: Density) => void] {
  const [density, setDensity] = useState<Density>(
    () => (localStorage.getItem("evidite.density") as Density | null) ?? "dense"
  );
  useEffect(() => {
    document.documentElement.dataset.density = density;
    localStorage.setItem("evidite.density", density);
  }, [density]);
  return [density, setDensity];
}

/* -------------------------------------------------------------------------- */
/* Controls                                                                    */
/* -------------------------------------------------------------------------- */

export function SearchInput({
  value,
  onChange,
  placeholder,
  label,
  inputRef
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  label: string;
  inputRef?: RefObject<HTMLInputElement | null>;
}) {
  return (
    <div className="search">
      <SearchGlyph />
      <input
        ref={inputRef}
        type="search"
        value={value}
        aria-label={label}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
      <kbd aria-hidden="true">/</kbd>
    </div>
  );
}

function SearchGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" strokeLinecap="round" />
    </svg>
  );
}

export type SegmentedOption<T extends string> = {
  value: T;
  label: ReactNode;
  count?: number;
  /** Tooltip, for icon-only options (which carry their name in `label`). */
  title?: string;
};

/**
 * A single choice from a few options, drawn as a segmented control. It is a
 * radio group (ARIA radio-group pattern): one tab stop, the arrow keys move and
 * choose, Home and End go to the ends. `via` is "pointer" only when a pointer press made the
 * choice; anything else (arrows, Space, Enter, assistive technology) is "keyboard", so callers
 * leave focus where it is unless they know a pointer moved it.
 */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  className
}: {
  label: string;
  value: T;
  options: SegmentedOption<T>[];
  onChange: (next: T, via: "pointer" | "keyboard") => void;
  className?: string;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  // The option a pointer is pressing, cleared by its click, the end of the gesture or any key.
  const pressed = useRef<string | null>(null);
  const current = options.findIndex((option) => option.value === value);
  const choose = (index: number) => {
    const option = options[index];
    if (!option) return;
    refs.current[index]?.focus();
    if (option.value !== value) onChange(option.value, "keyboard");
  };
  return (
    <div
      className={className ? `segmented ${className}` : "segmented"}
      role="radiogroup"
      aria-label={label}
      onKeyDown={(event) => {
        pressed.current = null;
        const last = options.length - 1;
        const from = Math.max(0, current);
        const next =
          event.key === "ArrowRight" || event.key === "ArrowDown"
            ? from === last
              ? 0
              : from + 1
            : event.key === "ArrowLeft" || event.key === "ArrowUp"
              ? from === 0
                ? last
                : from - 1
              : event.key === "Home"
                ? 0
                : event.key === "End"
                  ? last
                  : undefined;
        if (next === undefined) return;
        event.preventDefault();
        choose(next);
      }}
    >
      {options.map((option, index) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked || (current < 0 && index === 0) ? 0 : -1}
            title={option.title}
            onPointerDown={() => {
              // Only this option's own press counts, and only until the gesture ends: a press
              // dragged off or cancelled must not make a later assistive-tech click "pointer".
              // The click follows pointerup in the same task, so a later task can clear it.
              pressed.current = option.value;
              const end = () => {
                window.removeEventListener("pointerup", end, true);
                window.removeEventListener("pointercancel", end, true);
                setTimeout(() => {
                  if (pressed.current === option.value) pressed.current = null;
                });
              };
              window.addEventListener("pointerup", end, true);
              window.addEventListener("pointercancel", end, true);
            }}
            onClick={() => {
              const via = pressed.current === option.value ? "pointer" : "keyboard";
              pressed.current = null;
              onChange(option.value, via);
            }}
          >
            {option.label}
            {option.count !== undefined && <b>{option.count}</b>}
          </button>
        );
      })}
    </div>
  );
}

export function Field({
  label,
  hint,
  htmlFor,
  children
}: {
  label: string;
  hint?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={htmlFor}>
        {label}
        {hint !== undefined && <span className="hint">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Feedback                                                                    */
/* -------------------------------------------------------------------------- */

export function Notice({
  tone,
  children,
  action,
  className,
  quiet = false
}: {
  tone: "error" | "success" | "warn" | "info";
  children: ReactNode;
  action?: { label: string; onClick: () => void };
  className?: string;
  /** Static context rather than an announcement: no live-region role. */
  quiet?: boolean;
}) {
  const Icon = tone === "success" ? CircleCheck : tone === "warn" ? TriangleAlert : CircleAlert;
  return (
    <div
      className={`notice${className ? ` ${className}` : ""}`}
      data-tone={tone}
      role={tone === "error" ? "alert" : quiet ? undefined : "status"}
    >
      <Icon aria-hidden="true" />
      <div>{children}</div>
      {action && (
        <button type="button" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  );
}

export function Empty({
  icon: Icon,
  title,
  children,
  action
}: {
  icon: React.ComponentType<{ "aria-hidden"?: boolean }>;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <Icon aria-hidden={true} />
      <b>{title}</b>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function PanelEmpty({
  icon: Icon,
  title,
  children
}: {
  icon: React.ComponentType<{ "aria-hidden"?: boolean }>;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="panel-empty">
      <Icon aria-hidden={true} />
      <b>{title}</b>
      {children && <p>{children}</p>}
    </div>
  );
}

export function SkeletonRows({ columns, rows = 8 }: { columns: number; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, row) => (
        <tr key={row} aria-hidden="true">
          {Array.from({ length: columns }, (_, column) => (
            <td key={column}>
              <span className="skeleton" style={{ width: `${column === 0 ? 62 : 38}%` }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function PanelLoading() {
  return (
    <div className="panel-loading" aria-label="Loading record" role="status">
      <span className="skeleton" style={{ width: "58%", height: 17 }} />
      <span className="skeleton" style={{ width: "76%" }} />
      <span className="skeleton" style={{ width: "40%" }} />
      <span className="skeleton" style={{ width: "88%", height: 44 }} />
      <span className="skeleton" style={{ width: "66%" }} />
    </div>
  );
}

export function BackBar({ label, onBack }: { label: string; onBack: () => void }) {
  return (
    <div className="back-bar">
      <button type="button" className="btn btn-quiet" onClick={onBack}>
        <ArrowLeft aria-hidden="true" /> {label}
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Status                                                                      */
/* -------------------------------------------------------------------------- */

const accountTone: Record<string, "ok" | "warn" | "bad" | "neutral"> = {
  active: "ok",
  pending_activation: "warn",
  blocked: "bad",
  closed: "neutral"
};

export function StatusDot({
  value,
  tone
}: {
  value: string;
  tone?: "ok" | "warn" | "bad" | "neutral" | undefined;
}) {
  return (
    <span className="status" data-tone={tone ?? accountTone[value] ?? "neutral"}>
      <i aria-hidden="true" />
      {humanize(value)}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Keyboard: list traversal                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Arrow-key traversal over a record list in a native table. Returns a keydown
 * handler to place on the scroll container and `rowProps` for each body row.
 *
 * Roles: the open row is marked `aria-current="true"`, not `aria-selected`,
 * which is only valid inside a grid, treegrid or listbox; the native table keeps
 * its column headers for screen readers. Every row is in the tab order (a table
 * is not a grid, so Tab must reach each record). The arrow keys move from the
 * focused row, or from the open one when focus is elsewhere, and focus follows
 * so each new row is read out.
 */
export function useListKeyboard<T extends { id: string }>({
  items,
  selectedId,
  onSelect
}: {
  items: T[];
  selectedId: string | undefined;
  onSelect: (id: string) => void;
}) {
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());

  const registerRow = useCallback((id: string, node: HTMLTableRowElement | null) => {
    if (node) rowRefs.current.set(id, node);
    else rowRefs.current.delete(id);
  }, []);

  const move = useCallback(
    (delta: number, focus = false, fromId = selectedId) => {
      if (!items.length) return;
      const current = items.findIndex((item) => item.id === fromId);
      const next = current < 0 ? 0 : Math.min(items.length - 1, Math.max(0, current + delta));
      const target = items[next];
      if (!target) return;
      onSelect(target.id);
      const row = rowRefs.current.get(target.id);
      if (focus) row?.focus({ preventScroll: true });
      row?.scrollIntoView({ block: "nearest" });
    },
    [items, selectedId, onSelect]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const target = event.target as HTMLElement;
      if (target.closest("input, textarea, select, [contenteditable='true']")) return;
      event.preventDefault();
      // From a focused row, move from that row (Tab may have left it apart from the open
      // one) and take focus along; from anywhere else, move from the open row and leave focus.
      const row = target.tagName === "TR" ? target : undefined;
      const fromId = row ? [...rowRefs.current].find(([, node]) => node === row)?.[0] : undefined;
      move(event.key === "ArrowDown" ? 1 : -1, Boolean(row), fromId ?? selectedId);
    },
    [move]
  );

  const rowProps = (id: string) => ({
    ref: (node: HTMLTableRowElement | null) => registerRow(id, node),
    "aria-current": id === selectedId ? ("true" as const) : undefined,
    // Every row stays a tab stop: a native table is not a grid, so Tab must reach each record.
    tabIndex: 0,
    onClick: () => onSelect(id),
    onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
      if (event.target !== event.currentTarget) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onSelect(id);
      }
    }
  });

  return { onKeyDown, registerRow, rowProps, move };
}

export type RowProps = ReturnType<typeof useListKeyboard>["rowProps"];

/** True below the tablet breakpoint, so views can render a compact column set. */
export function useIsNarrow(query = "(max-width: 700px)") {
  const [narrow, setNarrow] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setNarrow(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [query]);
  return narrow;
}

/**
 * Focus the search field on "/" and on load. Escape returns focus to it.
 */
export function useSearchShortcut(
  ref: RefObject<HTMLInputElement | HTMLTextAreaElement | null>,
  { focusOnMount = true }: { focusOnMount?: boolean } = {}
) {
  useEffect(() => {
    if (focusOnMount) ref.current?.focus();
    function onKey(event: KeyboardEvent) {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (target?.isContentEditable) return;
      event.preventDefault();
      ref.current?.focus();
      ref.current?.select();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ref, focusOnMount]);
}
